import React, { useState } from 'react';
import { IconButton, Dialog, DialogTitle, DialogActions, Button, Typography, Box } from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Simplified color constants
const COLORS = {
    primary: '#083D61',
    primaryHover: '#062b45',
    white: '#ffffff'
};

// Reusable accordion styles
const accordionStyles = {
    accordion: { mb: 1, borderRadius: '5px' },
    summary: {
        backgroundColor: COLORS.primary,
        borderRadius: '5px',
        '& .MuiTypography-root': {
            fontSize: '1.125rem',
            color: COLORS.white
        }
    },
    details: { p: 2 }
};

const FAQModal = ({ title }) => {
    const [openDialog, setOpenDialog] = useState(false);

    const handleClickOpen = () => {
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    return (
        <>
            <Box 
                onClick={handleClickOpen}
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    color: COLORS.white
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    className="titillium-web project-header-title-text"
                    sx={{ 
                        textDecoration: 'underline', 
                        fontSize: '1rem',
                        mr: 1
                    }}
                >
                    FAQs
                </Typography>
                <QuizIcon sx={{ fontSize: 'large', marginRight: 1 }} />
            </Box>
            <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="xl" fullWidth>
                <DialogTitle 
                    sx={{
                        fontSize: '1.875rem', 
                        fontWeight: 'bold', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center'
                    }}
                >
                    {title}
                    <IconButton 
                        onClick={handleDialogClose} 
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Box sx={{ 
                    width: '95%',
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 2
                }}>
                {/* Common accordion styling */}
                <Accordion sx={accordionStyles.accordion}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.white }} />}
                        sx={accordionStyles.summary}
                    >
                        <Typography>Project Access Permissions & Eligibility</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionStyles.details}>
                        <Typography sx={{ mb: 2 }}>
                            Access to projects within this application is determined by company, user role, and department. The criteria are as follows:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2 }}>
                            <li>
                                <Typography component="span" sx={{ textDecoration: 'underline' }}>
                                    If you're a non-AMN user:
                                </Typography> You only see the projects where you're a member on the Project Roles in Procore.
                            </li>
                            <li>
                                <Typography component="span" sx={{ textDecoration: 'underline' }}>
                                    If you're an AMN user:
                                </Typography>
                                <Box component="ul" sx={{ mt: 1 }}>
                                    <li>In ARCOnnect, if your department is one of the following: <em>"Executives/RMs", "Technology", "Innovation", "Operational Management", "Accounting", "Safety", "Legal", "Human Resources", "Business Development", "Preconstruction"</em>, you can access all projects.</li>
                                    <li>Otherwise, you only access the projects where you're a member on the Project Roles in Procore.</li>
                                </Box>
                            </li>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={accordionStyles.accordion}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.white }} />}
                        sx={accordionStyles.summary}
                    >
                        <Typography>Why isn't my project showing up?</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionStyles.details}>
                        <Typography sx={{ mb: 2 }}>
                            This application only pulls in projects that meet the following criteria:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                            <li>The project has both Procore and Salesforce records.</li>
                            <li>The project has a Salesforce probability of 95% or higher.</li>
                            <li>The project has not been inactive in Procore.</li>
                        </Box>
                        <Typography>
                            If your project meets this criteria and is still not showing up, please email our team at{' '}
                            <a href='mailto:innovation@arcomurray.com?subject=PHD Feedback' target='_blank' rel="noreferrer">
                                innovation@arcomurray.com
                            </a>.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={accordionStyles.accordion}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.white }} />}
                        sx={accordionStyles.summary}
                    >
                        <Typography>My project's data is inaccurate.</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionStyles.details}>
                        <Typography sx={{ mb: 2 }}>
                            Click the info icons (top right of each tile) to see the data source(s). Changes will be synced and reflected at various times based on the data source:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2 }}>
                            <li>Salesforce and Procore changes will be reflected in this dashboard within 5 minutes (with the exception of Budget Attributes, which is weekly, but can be manually synced).</li>
                            <li>Budget snapshot data is updated every 5 minutes.</li>
                            <li>Viewpoint data is synced daily at 4am.</li>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={accordionStyles.accordion}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.white }} />}
                        sx={accordionStyles.summary}
                    >
                        <Typography>Why is my client name showing up differently than it does in Procore?</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionStyles.details}>
                        <Typography>
                            Client names are sourced from Salesforce. Your client's account name may be different in Salesforce than it is in Procore. If you believe that it should be changed, please verify with your team's Ops Leader before reaching out to make the update.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={accordionStyles.accordion}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.white }} />}
                        sx={accordionStyles.summary}
                    >
                        <Typography>My project's Concur Tracker isn't loading.</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionStyles.details}>
                        <Typography>
                            You may be facing this issue if you have not registered for a Power BI account. Try accessing the main Power BI website at{' '}
                            <a href="https://app.powerbi.com/?pbi_source=websignin_uNav" target="_blank" rel="noreferrer">
                                https://app.powerbi.com/?pbi_source=websignin_uNav
                            </a>{' '}
                            to register your Power BI account to your Office 365 profile. Then, try refreshing the page and opening the dashboard again. You will only need to do this once to register yourself on Power BI.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={accordionStyles.accordion}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.white }} />}
                        sx={accordionStyles.summary}
                    >
                        <Typography>My Operations View isn't loading. I'm stuck on a Power BI loading screen.</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionStyles.details}>
                        <Typography>
                            You may be facing this issue if you have not registered for a Power BI account. Try accessing the main Power BI website at{' '}
                            <a href="https://app.powerbi.com/?pbi_source=websignin_uNav" target="_blank" rel="noreferrer">
                                https://app.powerbi.com/?pbi_source=websignin_uNav
                            </a>{' '}
                            to register your Power BI account to your Office 365 profile. Then, try refreshing the page and opening the dashboard again. You will only need to do this once to register yourself on Power BI.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion sx={accordionStyles.accordion}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: COLORS.white }} />}
                        sx={accordionStyles.summary}
                    >
                        <Typography>How do I request a new feature?</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={accordionStyles.details}>
                        <Typography>
                            If you have an idea for a new feature or improvement, please email our team at{' '}
                            <a href='mailto:innovation@arcomurray.com?subject=Feature Request' target='_blank' rel="noreferrer">
                                innovation@arcomurray.com
                            </a>. Include as much detail as possible about your request to help us understand your needs.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                </Box>
                <DialogActions>
                    <Button
                        onClick={() => {
                            window.location.href = `mailto:innovation@arcomurray.com?subject=${encodeURIComponent("Question on PHD")}`;
                        }} 
                        variant="contained"
                        sx={{
                            backgroundColor: COLORS.primary,
                            '&:hover': { backgroundColor: COLORS.primaryHover }
                        }}
                    >
                        Contact Us
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FAQModal;
